import { DEV_URL, rdelete, rget, rpost } from "utils/requests";

const apiUrl = (path) => `${DEV_URL}${path}`;

const ok = (response) => response && response.status >= 200 && response.status < 300;

const errorMessageFrom = (response, fallback = "Utility request failed") =>
  response?.data?.message ||
  response?.data?.Message ||
  Object.values(response?.data?.errors ?? response?.data?.Errors ?? {}).flat().filter((value) => typeof value === "string").join(" ") ||
  response?.data?.detail ||
  response?.data?.title ||
  response?.data?.error ||
  response?.data?.Error ||
  response?.statusText ||
  fallback;

const unwrap = (response, fallback) => {
  if (!ok(response)) {
    const error = new Error(errorMessageFrom(response, fallback));
    error.status = response?.status;
    error.data = response?.data;
    throw error;
  }
  return response.data;
};

const bulkUtilityOperation = async (path, payload, fallback) => {
  const response = await rpost(apiUrl(path), payload);
  const data = response?.data;
  const success = data?.isSuccess ?? data?.IsSuccess;
  if (!ok(response) || success === false) {
    const error = new Error(errorMessageFrom(response, fallback));
    error.status = response?.status;
    error.data = data;
    throw error;
  }
  return data;
};

const asArray = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.result)) return data.result;
  if (Array.isArray(data?.Result)) return data.Result;
  if (Array.isArray(data?.Data)) return data.Data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.Value)) return data.Value;
  if (Array.isArray(data?.value)) return data.value;
  if (Array.isArray(data?.Items)) return data.Items;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.States)) return data.States;
  if (Array.isArray(data?.states)) return data.states;
  if (Array.isArray(data?.Utilities)) return data.Utilities;
  if (Array.isArray(data?.utilities)) return data.utilities;
  return [];
};

const normalizeUtilityState = (state = {}) => ({
  ...state,
  id: state.id ?? state.Id,
  stateId: state.stateId ?? state.StateId,
  stateName: state.stateName ?? state.StateName,
  commodityId: state.commodityId ?? state.CommodityId,
  electric: state.electric ?? state.Electric,
  gas: state.gas ?? state.Gas ?? state.natGas ?? state.NatGas,
  natGas: state.natGas ?? state.NatGas,
  enerexStateId: state.enerexStateId ?? state.EnerexStateId,
  enerexUtilityStateId: state.enerexUtilityStateId ?? state.EnerexUtilityStateId,
});

const normalizeCountry = (country = {}) => ({
  ...country,
  id: country.id ?? country.Id ?? country.countryIdentifier ?? country.CountryIdentifier,
  Id: country.Id ?? country.id ?? country.countryIdentifier ?? country.CountryIdentifier,
  name: country.name ?? country.Name ?? country.countryName ?? country.CountryName ?? "",
  Name: country.Name ?? country.name ?? country.countryName ?? country.CountryName ?? "",
  countryIdentifier: country.countryIdentifier ?? country.CountryIdentifier ?? country.id ?? country.Id,
  CountryIdentifier: country.CountryIdentifier ?? country.countryIdentifier ?? country.Id ?? country.id,
  identifier: country.identifier ?? country.Identifier ?? country.countryIdentifier ?? country.CountryIdentifier,
  Identifier: country.Identifier ?? country.identifier ?? country.CountryIdentifier ?? country.countryIdentifier,
});

const normalizeState = (state = {}) => ({
  ...state,
  id: state.id ?? state.stateId ?? state.StateId ?? state.Id,
  Id: state.Id ?? state.stateId ?? state.StateId ?? state.id,
  stateId: state.stateId ?? state.StateId ?? state.id ?? state.Id,
  StateId: state.StateId ?? state.stateId ?? state.Id ?? state.id,
  name: state.name ?? state.Name ?? state.fullName ?? state.FullName ?? state.stateName ?? state.StateName ?? state.code ?? state.Code ?? "",
  Name: state.Name ?? state.name ?? state.fullName ?? state.FullName ?? state.stateName ?? state.StateName ?? state.code ?? state.Code ?? "",
  stateName: state.stateName ?? state.StateName ?? state.name ?? state.Name ?? "",
  StateName: state.StateName ?? state.stateName ?? state.Name ?? state.name ?? "",
  stateCode: state.stateCode ?? state.StateCode ?? state.code ?? state.Code ?? "",
  StateCode: state.StateCode ?? state.stateCode ?? state.Code ?? state.code ?? "",
  countryIdentifier: state.countryIdentifier ?? state.CountryIdentifier ?? state.countryId ?? state.CountryId,
  CountryIdentifier: state.CountryIdentifier ?? state.countryIdentifier ?? state.CountryId ?? state.countryId,
  countryId: state.countryId ?? state.CountryId ?? state.countryIdentifier ?? state.CountryIdentifier,
  CountryId: state.CountryId ?? state.countryId ?? state.CountryIdentifier ?? state.countryIdentifier,
});

const filterStatesByCountry = (states, countryIdentifier) => {
  if (!countryIdentifier) return states;

  const countryKey = String(countryIdentifier).toLowerCase();
  const statesWithCountry = states.filter((state) => [
    state.countryIdentifier,
    state.CountryIdentifier,
    state.countryId,
    state.CountryId,
    state.countryCode,
    state.CountryCode,
    state.countryName,
    state.CountryName,
    state.country,
    state.Country,
  ].some((value) => value !== undefined && value !== null && value !== ""));

  if (!statesWithCountry.length) return states;

  return statesWithCountry.filter((state) => {
    const candidates = [
      state.countryIdentifier,
      state.CountryIdentifier,
      state.countryId,
      state.CountryId,
      state.countryCode,
      state.CountryCode,
      state.countryName,
      state.CountryName,
      state.country,
      state.Country,
    ]
      .filter((value) => value !== undefined && value !== null && value !== "")
      .map((value) => String(value).toLowerCase());

    return candidates.includes(countryKey);
  });
};

const normalizeUtility = (utility = {}) => {
  const states = utility.utilityStates || utility.UtilityStates || [];
  return {
    ...utility,
    id: utility.id ?? utility.Id,
    Id: utility.Id ?? utility.id,
    enerexUtilityId: utility.enerexUtilityId ?? utility.EnerexUtilityId ?? utility.enerexIdentifier ?? utility.EnerexIdentifier,
    EnerexUtilityId: utility.EnerexUtilityId ?? utility.enerexUtilityId ?? utility.EnerexIdentifier ?? utility.enerexIdentifier,
    enerexIdentifier:
      utility.enerexIdentifier ||
      utility.EnerexIdentifier ||
      utility.enerexUtilityId ||
      utility.EnerexUtilityId ||
      utility.identifier ||
      utility.Identifier,
    EnerexIdentifier:
      utility.EnerexIdentifier ||
      utility.enerexIdentifier ||
      utility.EnerexUtilityId ||
      utility.enerexUtilityId ||
      utility.Identifier ||
      utility.identifier,
    name: utility.name || utility.Name || utility.shortName || utility.ShortName || "",
    longName:
      utility.longName ||
      utility.LongName ||
      utility.legalName ||
      utility.LegalName ||
      utility.Name ||
      utility.name ||
      "",
    webSite: utility.webSite || utility.WebSite || utility.website || utility.Website || "",
    WebSite: utility.WebSite || utility.webSite || utility.Website || utility.website || "",
    duns: utility.duns || utility.Duns || utility.dunsNumber || utility.DunsNumber || "",
    Duns: utility.Duns || utility.duns || utility.DunsNumber || utility.dunsNumber || "",
    logoURL:
      utility.logoURL ||
      utility.LogoURL ||
      utility.logoUrl ||
      utility.LogoUrl ||
      utility.logo ||
      utility.Logo ||
      "",
    parentUtilityName:
      utility.parentUtilityName || utility.ParentUtilityName || utility.parent?.Name || "",
    mergedWithUtilityName:
      utility.mergedWithUtilityName ||
      utility.MergedWithUtilityName ||
      utility.mergedWith?.Name ||
      "",
    parentEXID:
      utility.parentEXID ||
      utility.ParentEXID ||
      utility.parentEnerexId ||
      utility.ParentEnerexId ||
      "",
    parentEnerexId: utility.parentEnerexId ?? utility.ParentEnerexId ?? utility.parentEXID ?? utility.ParentEXID ?? "",
    ParentEnerexId: utility.ParentEnerexId ?? utility.parentEnerexId ?? utility.ParentEXID ?? utility.parentEXID ?? "",
    mergedWithEXID:
      utility.mergedWithEXID ||
      utility.MergedWithEXID ||
      utility.enerexMergedWithId ||
      utility.EnerexMergedWithId ||
      "",
    enerexMergedWithId:
      utility.enerexMergedWithId ?? utility.EnerexMergedWithId ?? utility.mergedWithEXID ?? utility.MergedWithEXID ?? "",
    EnerexMergedWithId:
      utility.EnerexMergedWithId ?? utility.enerexMergedWithId ?? utility.MergedWithEXID ?? utility.mergedWithEXID ?? "",
    parentUtilityId: utility.parentUtilityId ?? utility.ParentUtilityId ?? 0,
    ParentUtilityId: utility.ParentUtilityId ?? utility.parentUtilityId ?? 0,
    mergedWithUtilityId: utility.mergedWithUtilityId ?? utility.MergedWithUtilityId ?? 0,
    MergedWithUtilityId: utility.MergedWithUtilityId ?? utility.mergedWithUtilityId ?? 0,
    PurchaseOfReceivables: utility.PurchaseOfReceivables ?? utility.purchaseOfReceivables ?? false,
    active: utility.active ?? utility.Active ?? true,
    utilityStates: states.map(normalizeUtilityState),
  };
};

const normalizeAlias = (alias = {}) => ({
  ...alias,
  id: alias.id ?? alias.Id,
  Id: alias.Id ?? alias.id,
  alias: alias.alias || alias.Alias || alias.name || alias.Name || "",
  name: alias.name || alias.Name || alias.alias || alias.Alias || "",
  stateId: alias.stateId ?? alias.StateId ?? alias.stateID ?? alias.StateID,
  StateId: alias.StateId ?? alias.stateId ?? alias.StateID ?? alias.stateID,
  stateName: alias.stateName || alias.StateName || "",
  StateName: alias.StateName || alias.stateName || "",
});

const normalizeRateClass = (rateClass = {}) => ({
  ...rateClass,
  id: rateClass.id ?? rateClass.Id,
  Id: rateClass.Id ?? rateClass.id,
  stateId: rateClass.stateId ?? rateClass.StateId,
  utilityState:
    rateClass.utilityState ||
    rateClass.UtilityState ||
    rateClass.stateName ||
    rateClass.StateName ||
    "",
  commodityId: rateClass.commodityId ?? rateClass.CommodityId,
  commodityName: rateClass.commodityName || rateClass.CommodityName || "",
  rateClass:
    rateClass.rateClass ||
    rateClass.RateClass ||
    rateClass.rateClassName ||
    rateClass.RateClassName ||
    "",
});

const firstStateValue = (value) => {
  if (!Array.isArray(value) || value.length === 0) return undefined;
  const first = value.find((entry) => entry !== undefined && entry !== null);
  if (first && typeof first === "object") {
    return (
      first.id ??
      first.Id ??
      first.stateId ??
      first.StateId ??
      first.name ??
      first.Name ??
      first.stateName ??
      first.StateName ??
      first.utilityState ??
      first.UtilityState ??
      first.utilityStateName ??
      first.UtilityStateName ??
      first.code ??
      first.Code ??
      first.abbreviation ??
      first.Abbreviation
    );
  }
  return first;
};

const normalizeFormat = (format = {}) => ({
  ...format,
  id: format.id ?? format.Id,
  Id: format.Id ?? format.id,
  stateId:
    format.stateId ??
    format.StateId ??
    firstStateValue(format.stateIds) ??
    firstStateValue(format.StateIds) ??
    firstStateValue(format.utilityStates) ??
    firstStateValue(format.UtilityStates) ??
    format.utilityStateId ??
    format.UtilityStateId,
  StateId:
    format.StateId ??
    format.stateId ??
    firstStateValue(format.StateIds) ??
    firstStateValue(format.stateIds) ??
    firstStateValue(format.UtilityStates) ??
    firstStateValue(format.utilityStates) ??
    format.UtilityStateId ??
    format.utilityStateId,
  stateName:
    format.stateName ||
    format.StateName ||
    format.utilityState ||
    format.UtilityState ||
    format.utilityStateName ||
    format.UtilityStateName ||
    format.state ||
    format.State ||
    format.name ||
    format.Name ||
    firstStateValue(format.utilityStates) ||
    firstStateValue(format.UtilityStates) ||
    "",
  StateName:
    format.StateName ||
    format.stateName ||
    format.UtilityState ||
    format.utilityState ||
    format.UtilityStateName ||
    format.utilityStateName ||
    format.State ||
    format.state ||
    format.Name ||
    format.name ||
    firstStateValue(format.UtilityStates) ||
    firstStateValue(format.utilityStates) ||
    "",
  utilityState: format.utilityState ?? format.UtilityState,
  UtilityState: format.UtilityState ?? format.utilityState,
  utilityStateName: format.utilityStateName ?? format.UtilityStateName,
  UtilityStateName: format.UtilityStateName ?? format.utilityStateName,
  commodityId: format.commodityId ?? format.CommodityId,
});

const getTotal = (data) =>
  data?.iTotalRecords ??
  data?.ITotalRecords ??
  data?.totalRecords ??
  data?.TotalRecords ??
  data?.totalCount ??
  data?.TotalCount ??
  data?.total ??
  data?.Total ??
  asArray(data).length ??
  0;

export const getUtilityOptions = async () => {
  const data = await unwrap(await rget(apiUrl("/api/Utility")));
  return asArray(data).map(normalizeUtility);
};

export const getUtilities = async (filters) => {
  const data = await unwrap(
    await rpost(apiUrl("/api/Utility/GetUtilitiesPaginated"), filters),
    "Failed to load utilities"
  );

  return {
    rows: asArray(data).map(normalizeUtility),
    total: getTotal(data),
  };
};

export const getUtilityByIdentifier = async (identifier) =>
  normalizeUtility(
    await unwrap(
      await rget(apiUrl(`/api/Utility/GetByIdentifier/${identifier}`)),
      "Failed to load utility details"
    )
  );

export const saveUtility = async (payload) =>
  unwrap(
    await rpost(apiUrl("/api/Utility/AddUtility"), payload),
    "Failed to save utility"
  );

export const deactivateUtility = async (identifier, username = "DataHub") =>
  unwrap(
    await rpost(apiUrl("/api/Utility/DeactivateUtility"), {
      enerexUtilityId: identifier,
      username,
    }),
    "Failed to deactivate utility"
  );

export const activateUtility = async (identifier, username = "DataHub") =>
  unwrap(
    await rpost(apiUrl("/api/Utility/ActivateUtility"), {
      enerexUtilityId: identifier,
      username,
    }),
    "Failed to activate utility"
  );

export const getCountries = async () =>
  asArray(
    await unwrap(
      await rget(apiUrl("/api/Utility/GetCountries")),
      "Failed to load countries"
    )
  ).map(normalizeCountry);

export const getStates = async () =>
  asArray(
    await unwrap(
      await rget(apiUrl("/api/Utility/GetStates")),
      "Failed to load states"
    )
  ).map(normalizeState);

export const getStatesByCountry = async (countryIdentifier) => {
  if (!countryIdentifier) return getStates();

  try {
    const states = asArray(
      await unwrap(
        await rget(apiUrl("/api/Utility/GetStates"), {
          params: { countryIdentifier },
        }),
        "Failed to load states by country"
      )
    ).map(normalizeState);

    return filterStatesByCountry(states, countryIdentifier);
  } catch (error) {
    console.warn("Failed to load states by country, falling back to all states", error);
  }

  const states = await getStates();
  return filterStatesByCountry(states, countryIdentifier);
};

export const saveUtilityStateOperation = async (payload) => {
  const response = await rpost(apiUrl("/api/Utility/AddUtilityStateOfOperation"), payload);
  if (!ok(response) || response?.data?.isSuccess === false || response?.data?.IsSuccess === false) {
    const error = new Error(errorMessageFrom(response, "Failed to save state operation"));
    error.status = response?.status;
    error.data = response?.data?.data ?? response?.data?.Data;
    throw error;
  }
  return response.data;
};

export const removeUtilityStateOperation = async (id) =>
  unwrap(
    await rdelete(apiUrl(`/api/Utility/RemoveUtilitySOP/${id}`)),
    "Failed to delete state operation"
  );

export const getUtilityStateOperations = async (identifier) => {
  const data = await unwrap(
    await rget(apiUrl(`/api/Utility/GetUtilityStateOfOperations/${identifier}`)),
    "Failed to load state operations"
  );
  const rows = asArray(data);
  const utilityStates = rows[0]?.utilityStates || rows[0]?.UtilityStates || rows;
  return utilityStates.map(normalizeUtilityState);
};

export const createUtilityAlias = async (payload) =>
  unwrap(
    await rpost(apiUrl("/api/Utility/CreateAlias"), payload),
    "Failed to save alias"
  );

export const removeUtilityAlias = async (id, username = "DataHub") =>
  unwrap(
    await rget(apiUrl(`/api/Utility/RemoveAlias/${id}/${username}`)),
    "Failed to delete alias"
  );

export const getUtilityAliases = async (identifier) => {
  const data = await unwrap(
    await rget(apiUrl(`/api/Utility/GetUtilityAliasByUtilityIdentifier/${identifier}`)),
    "Failed to load aliases"
  );
  const rows = asArray(data);
  const aliases = rows[0]?.aliases || rows[0]?.Aliases || rows;
  return aliases.map(normalizeAlias);
};

export const getUtilityRateClasses = async (identifier) =>
  asArray(
    await unwrap(
      await rget(apiUrl(`/api/Utility/GetUtilityRateClassesByUtilityIdentifier/${identifier}`)),
      "Failed to load rate classes"
    )
  ).map(normalizeRateClass);

export const saveUtilityRateClass = async (payload) =>
  unwrap(
    await rpost(apiUrl("/api/Utility/AddUtilityRateClass"), payload),
    "Failed to save rate class"
  );

export const removeUtilityRateClasses = async (payload) =>
  bulkUtilityOperation(
    "/api/Utility/RemoveUtilityRateClasses",
    payload,
    "Failed to delete rate classes"
  );

export const removeUtilityAliases = async (payload) =>
  bulkUtilityOperation(
    "/api/Utility/RemoveUtilityAliases",
    payload,
    "Failed to delete aliases"
  );

export const transferUtilityRateClasses = async (payload) =>
  bulkUtilityOperation(
    "/api/Utility/TransferUtilityRateClasses",
    payload,
    "Failed to transfer rate classes"
  );

export const transferUtilityAliases = async (payload) =>
  bulkUtilityOperation(
    "/api/Utility/TransferUtilityAliases",
    payload,
    "Failed to transfer aliases"
  );

export const getUtilityFormats = async (identifier) =>
  asArray(
    await unwrap(
      await rget(apiUrl(`/api/Utility/GetUtilityFormatUtilityEXID/${identifier}`)),
      "Failed to load utility formats"
    )
  ).map(normalizeFormat);

export const saveUtilityFormat = async (payload) =>
  unwrap(
    await rpost(apiUrl("/api/Utility/AddUtilityFormat"), payload),
    "Failed to save utility format"
  );

export const removeUtilityFormat = async (id, username = "DataHub") =>
  unwrap(
    await rget(apiUrl(`/api/Utility/RemoveUtilityFormat/${id}/${username}`)),
    "Failed to delete utility format"
  );

export const getUtilityActivityLog = async (identifier) => {
  const data = await unwrap(
    await rget(apiUrl(`/api/Utility/GetUtilityActivityLog/${identifier}`)),
    "Failed to load utility history"
  );
  return asArray(data);
};

export const getUtilityStatesByCommodity = async (identifier, commodityId = 0) =>
  asArray(
    await unwrap(
      await rget(
        apiUrl(`/api/Utility/GetUtilityStateOfOperationsById/${identifier}/${commodityId}`)
      ),
      "Failed to load utility states"
    )
  ).map(normalizeUtilityState);
